/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom} from 'rxjs';
import {Component, Inject, OnInit, signal} from '@angular/core';
import {resourceOptionParams} from '../common/controller/signal-admin-resource-api';
import {BsModalRef} from 'ngx-bootstrap/modal';
import * as _ from 'lodash';

import template from './mp-product-modal.component.html';
import {ADVERTISING_SHARED} from '../advertising/advertising-shared';
import {MarketplaceProductApiService} from '../admin/marketplace-product/marketplace-product-api.service';
import {interpolateTemplate} from '../common/filters/interpolate-template';
import {deferSignalStateWrite} from '../common/util/template-state';
import {MarketplaceProductOptionsApiService} from './marketplace-product-options-api.service';

@Component({
  standalone: true,
  imports: [...ADVERTISING_SHARED],
  selector: 'adn-mp-product-modal',
  template
})
export class MpProductModalComponent implements OnInit {
  allProducts: any[] = [];
  initialAxProduct: any;
  isMarketplace = false;
  initialActiveTab: 'table' | 'block' = 'table';
  onSave: (axProduct: any, allAxProducts: any[]) => Promise<any>;
  onSaved: () => void;

  readonly allAxProducts = signal<any[]>([]);
  activeTab: 'table' | 'block' = 'table';
  readonly loading = signal(false);
  axProduct: any;
  readonly optionalTargetingTypes = signal<string[]>([]);
  readonly layoutCategories = signal<string[]>([]);
  readonly labels = signal<string[]>([]);
  readonly adSizes = signal<string[]>([]);
  optionalTargetingType: string;
  layoutCategory: string;
  label: string;
  adSize: string;
  readonly form = signal<any>({});
  readonly disabled = signal(false);

  constructor(
    public bsModalRef: BsModalRef,
    private readonly marketplaceProductOptionsApi: MarketplaceProductOptionsApiService,
    @Inject(MarketplaceProductApiService) private readonly MarketplaceProduct: MarketplaceProductApiService
  ) {
  }

  ngOnInit(): void {
    this.allAxProducts.set(this.allProducts || []);
    this.activeTab = this.initialActiveTab || (this.allAxProducts().length > 10 ? 'table' : 'block');
    this.axProduct = this.initialAxProduct;

    this.marketplaceProductOptionsApi.get().subscribe((data) => {
      // Defer filter option lists — *ngIf="layoutCategories.length > 1" etc.
      // NG0100 if filled during modal checkNoChanges.
      deferSignalStateWrite(() => {
        this.optionalTargetingTypes.set(data.optionalTargetingTypes);
        this.layoutCategories.set(data.layoutCategories);
        this.labels.set(data.labels);
        this.adSizes.set(_.orderBy(data.adSizes, (ad: string) => {
          return ad === 'ANY' ? 1 : parseInt(ad.split('x')[0], 10);
        }));
      });
    });
  }

  selectAxProduct(prod: any): void {
    this.axProduct = prod;
  }

  filter(): void {
    const params: any = {};
    if (this.optionalTargetingType) {
      params.optionalTargeting = this.optionalTargetingType;
    }
    if (this.layoutCategory) {
      params.layoutCategory = this.layoutCategory;
    }
    if (this.label) {
      params.label = this.label;
    }
    if (this.adSize) {
      params.dimensions = this.adSize === 'ANY' ? '0x0' : this.adSize;
    }
    this.loading.set(true);
    firstValueFrom(this.MarketplaceProduct.listOptions(resourceOptionParams(params))).then((data: any) => {
      deferSignalStateWrite(() => {
        this.allAxProducts.set(data.results);
        this.loading.set(false);
      });
    }, () => {
      deferSignalStateWrite(() => this.loading.set(false));
    });
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }

  saveAxProduct(): void {
    if (!this.axProduct) {
      this.dismiss();
      return;
    }
    if (!this.onSave) {
      this.dismiss();
      return;
    }

    this.disabled.set(true);
    this.form.set({});
    this.onSave(this.axProduct, this.allAxProducts()).then(() => {
      this.onSaved?.();
      this.bsModalRef.hide();
    }, (resp: any) => {
      deferSignalStateWrite(() => {
        this.form.set({
          validationError: true,
          apiErrors: _.map(_.get(resp, ['data', 'errors']), (errData: any) => {
            return {apiText: interpolateTemplate(errData.error.text)(errData.error.parameters)};
          })
        });
        this.disabled.set(false);
      });
    });
  }
}
